import './services/firebaseApp'

import React from 'react'
import { StyleProvider, I18NProvider, useEffect } from '@codeleap/common'
import { Settings, variantProvider, I18N } from './app'
import {
  AppStatusOverlay,
  DebugButton,
  GlobalStyle,
  NoReviewQuestionsModal,
  CongratulationsStories,
  variants,
  AchievementDetail,
  AchievementModal,
  PlayerYoutube,
  OnboardingQuestions,
  SignInNewsFeed,
  HereWeGoModal,
  ImpersonatingBanner,
  AchievementToast,
  useOpenOnboardingQuestions,
} from './components'
import { Provider } from 'react-redux'
import { store } from './redux'
import { APIClient, analytics, amplitude } from './services'
import CreateStory from './components/Modals/CreateStory'
import StoryDetails from './components/Modals/StoryDetail'
import { Location } from '@reach/router'
import { useGetUTMSourcesFromURL, useHandleOpen, usePopulateReferralCodeFromURL } from './utils'
import { globalHistory } from '@reach/router'
import { ModalOutlet } from '@codeleap/modals'
import { ImmersiveVideo } from './components/Player/Immersive'
import { QueryClientProvider } from '@tanstack/react-query'
import { Organisation } from '@/features'

amplitude.sessionReplayManager.initialize()

export const Overlays = () => (
  // This is needed for useLocation to work inside the modals
  <Location>
    {() => (
      <>
        <DebugButton />
        <Organisation.OrganisationInfo />
        <Organisation.UnknownOrgModal />
        <CreateStory />
        <NoReviewQuestionsModal />
        <StoryDetails />
        <OnboardingQuestions />
        <SignInNewsFeed />
        <CongratulationsStories />
        <AchievementDetail />
        <AchievementModal />
        <PlayerYoutube.Fullscreen />
        <HereWeGoModal />
        <AppStatusOverlay />
        <ModalOutlet />
        <ImpersonatingBanner />
      </>
    )}
  </Location>
)

const App = ({ children }) => {
  const authState = APIClient.Session.useAutoLogin()

  useHandleOpen({
    mapping: {
      openQuestions: undefined,
      questions: undefined,
      openCreateStory: 'createStory',
    },
    onOpen: () => Organisation.Pulse.ReviewOrganisationModal.toggle(),
  })

  ImmersiveVideo.useCaptureFirstInteraction()
  usePopulateReferralCodeFromURL()
  useGetUTMSourcesFromURL()
  useOpenOnboardingQuestions(authState.isLoggedIn ? authState.profile : undefined)

  useEffect(() => {
    globalHistory.listen(({ action }) => {
      if (authState.isLoggedIn && action === 'PUSH') {
        analytics?.track('logged_in_pageview')
      }
    })
  }, [globalHistory, authState.isLoggedIn])

  return (
    <>
      <GlobalStyle />
      {children}
      <Overlays />
    </>
  )
}

export const Root = ({ children }) => {
  return (
    <QueryClientProvider client={APIClient.queryClient.client}>
      <Provider store={store}>
        <StyleProvider settings={Settings} variantProvider={variantProvider} variants={variants} logger={logger}>
          <AchievementToast />

          <I18NProvider i18n={I18N}>
            <Location>
              {() => (
                <>
                  <App>{children}</App>
                </>
              )}
            </Location>
          </I18NProvider>
        </StyleProvider>
      </Provider>
    </QueryClientProvider>
  )
}
